import { StandardEvents } from '@shopify/events';

// Fetch only the drawer body: morphing the containing PDP would close the dialog.
class ActionRecommendations extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.dialog = this.closest('dialog');
    this.observer = new MutationObserver(() => {
      if (this.dialog.open) this.load();
    });
    if (this.dialog) this.observer.observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-recommendation-retry]')) this.load();
    }, { signal: this.abort.signal });
    if (this.dialog?.open) this.load();
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
  }
  async load() {
    if (this.hasAttribute('data-loaded') || this.loading) return;
    this.loading = true;
    this.setAttribute('aria-busy', 'true');
    const status = this.querySelector('[data-recommendation-status]');
    const retry = this.querySelector('[data-recommendation-retry]');
    if (status) status.textContent = 'Loading more lovely things…';
    if (retry) retry.hidden = true;
    try {
      const response = await fetch(this.dataset.url, { signal: this.abort.signal });
      if (!response.ok) throw new Error('Recommendations unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.getElementById(this.id);
      if (!content?.hasAttribute('data-loaded')) throw new Error('Missing recommendation content');
      if (!this.isConnected) return;
      // Keep a focused retry button stable until focus can return to the scrollable body.
      if (this.contains(document.activeElement)) this.closest('.volt-information-drawer__body')?.focus();
      const hasEmptyStatus = content.querySelector(':scope > p[role="status"]');
      if (status && !hasEmptyStatus) {
        status.classList.add('visually-hidden');
        this.replaceChildren(status, ...content.childNodes);
        status.textContent = 'More like this loaded.';
      } else {
        this.replaceChildren(...content.childNodes);
      }
      this.setAttribute('data-loaded', '');
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (status) status.textContent = 'We couldn’t load recommendations. Please try again.';
      if (retry) retry.hidden = false;
    } finally {
      this.loading = false;
      this.removeAttribute('aria-busy');
    }
  }
}

// The existing ESC app owns identity, saved state, list selection and all writes.
class ActionWishlist extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.section = this.closest('.shopify-section');
    this.generation = 0;
    this.section?.addEventListener(StandardEvents.productSelect, async (event) => {
      if (!event.target.closest('lazy-oaf-purchase:not([data-embedded])')) return;
      const generation = ++this.generation;
      try {
        const { detail } = await event.promise;
        // Let Horizon finish morphing its hidden variant input before notifying the legacy app.
        requestAnimationFrame(() => {
          if (!this.isConnected || generation !== this.generation || !detail?.resource?.id) return;
          window.ESCWishlist?.overwriteVariantId?.(detail.resource.id);
          this.section.querySelector('lazy-oaf-purchase:not([data-embedded]) input[name="id"]')
            ?.dispatchEvent(new Event('change', { bubbles: true }));
        });
      } catch { /* Keep the last provider state if variant selection fails. */ }
    }, { signal: this.abort.signal });
    this.section?.addEventListener('gift-card:variant-change', (event) => {
      const variantId = event.detail?.variant?.id;
      if (!variantId) return;
      window.ESCWishlist?.overwriteVariantId?.(variantId);
      this.section.querySelector('[data-gift-card-variant-id]')
        ?.dispatchEvent(new Event('change', { bubbles: true }));
    }, { signal: this.abort.signal });
    const mount = () => {
      if (!this.isConnected) return;
      window.reinitializeWishlistComponents?.();
      this.checkReady();
    };
    this.observer = new MutationObserver(() => this.checkReady());
    this.observer.observe(this, { childList: true, subtree: true });
    if (document.readyState === 'complete') mount();
    else window.addEventListener('load', mount, { once: true, signal: this.abort.signal });
    this.timer = setTimeout(() => {
      if (!this.providerReady) {
        const status = this.querySelector('[data-wishlist-status]');
        const fallback = this.querySelector('[data-wishlist-fallback]');
        if (fallback) fallback.hidden = false;
        if (status) {
          status.classList.add('visually-hidden');
          status.textContent = fallback?.textContent || 'Wishlist could not load.';
        }
      }
    }, 12000);
  }
  checkReady() {
    if (this.querySelector('.esc-wishlist__add-to-wishlist__button, .esc-wishlist__add-to-wishlist__icon-group__icon')) {
      this.providerReady = true;
      this.querySelector('[data-wishlist-status]').hidden = true;
      this.querySelector('[data-wishlist-fallback]').hidden = true;
      clearTimeout(this.timer);
    }
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    clearTimeout(this.timer);
  }
}
if (!customElements.get('lazy-oaf-action-recommendations')) customElements.define('lazy-oaf-action-recommendations', ActionRecommendations);
if (!customElements.get('lazy-oaf-action-wishlist')) customElements.define('lazy-oaf-action-wishlist', ActionWishlist);
